import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, Button, List, Typography, Empty, Spin, message } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import DirectOrderViewModal from './DirectOrderViewModal';
import DirectOrderEditModal from './DirectOrderEditModal';
import { useDirectOrder } from '../context/DirectOrderContext';

const { Text } = Typography;

const NotificationBell = ({ userEmail, userRole, placement = 'bottomRight', renderTrigger }) => {
  const { notifications, unreadCount, loading, fetchNotifications } = useNotifications();
  const { loadUserDirectOrders, userDirectOrders, getDirectOrderById } = useDirectOrder();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedDirectOrder, setSelectedDirectOrder] = useState(null);

  // Load user direct orders when component mounts
  useEffect(() => {
    if (userEmail) {
      loadUserDirectOrders();
    }
  }, [userEmail, loadUserDirectOrders]);

  // Helper function to determine notification type from notification data
  const getNotificationType = (notification) => {
    // If API provides a type field, use it directly
    if (notification.type) {
      return notification.type;
    }
    
    // Otherwise, determine from the data
    // Check if it has a rejection reason
    const hasRejection = notification.rejectionReason && notification.rejectionReason.trim() !== '';
    
    // Check if it was previously rejected but now updated
    const wasRejected = notification.wasRejected === true || notification.wasRejected === 1;
    
    // Check approval statuses
    const firstApproval = notification.do_firstApproval;
    const secondApproval = notification.do_secondApproval;
    const thirdApproval = notification.do_thirdApproval;
    
    // Determine the type based on the data
    if (hasRejection) {
      // If it has a rejection reason, it's a rejection
      return 'rejection';
    } else if (wasRejected && (firstApproval === 4 || secondApproval === 4 || thirdApproval === 4)) {
      // If it was rejected and now pending approval again, it's updated_after_rejection
      return 'updated_after_rejection';
    } else if (firstApproval === 4 || secondApproval === 4 || thirdApproval === 4) {
      // Status 4 means pending approval
      return 'pending_approval';
    } else {
      // Default to pending if we can't determine
      return 'pending_approval';
    }
  };

  const handleNotificationClick = async (notification) => {
    setDropdownOpen(false);
    
    try {
      // Determine notification type from the data
      const notificationType = getNotificationType(notification);
      
      
      // For pending_approval, navigate to dashboard's pending tab
      if (notificationType === 'pending_approval') {
        navigate('/direct-order/dashboard', { state: { activeTab: 'pending' } });
        return;
      }
      
      // For updated_after_rejection, navigate to dashboard's rejected tab
      if (notificationType === 'updated_after_rejection') {
        navigate('/direct-order/dashboard', { state: { activeTab: 'rejected' } });
        return;
      }
      
      // Only rejection type continues to open modal below
      if (notificationType !== 'rejection') {
        return;
      }
      
      // Get the order ID from notification - backend sends doId
      const orderId = notification.doId || notification.orderId || notification.id;
      
      if (!orderId) {
        message.error('Invalid notification - missing order ID');
        return;
      }
      
      // For rejection and updated_after_rejection, find order from user's orders
      
      // Load user orders if not already loaded
      if (!userDirectOrders || userDirectOrders.length === 0) {
        await loadUserDirectOrders();
      }
      
      // Find the order in user's orders
      const order = userDirectOrders.find(o => {
        const id = o.doId || o.orderId || o.id;
        return id === orderId;
      });
      
      if (order) {
        setSelectedDirectOrder(order);
        
        // Open edit modal for rejection, view modal for updated_after_rejection
        if (notificationType === 'rejection') {
          setEditModalVisible(true);
        } else if (notificationType === 'updated_after_rejection') {
          setViewModalVisible(true);
        }
      } else {
        message.error('Unable to load order details. Order not found.');
      }
      
    } catch (error) {
      message.error('Error loading order details.');
    }
  };

  // Function to handle closing view modal
  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedDirectOrder(null);
  };

  // Function to handle closing edit modal
  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedDirectOrder(null);
  };

  // Function to handle save from edit modal
  const handleSaveEditModal = () => {
    // Reload notifications and user direct orders to show updated data
    if (userEmail) {
      fetchNotifications(userEmail, userRole);
    }
    loadUserDirectOrders();
    setEditModalVisible(false);
    setSelectedDirectOrder(null);
  };

  const formatNotificationTime = (notification) => {
    const orderDate = notification.orderDate;
    
    if (!orderDate) return '';
    
    // Parse date at noon to avoid timezone issues
    const dateParts = orderDate.split('-');
    const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], 12, 0, 0);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    
    // Compare dates at noon to avoid timezone issues
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
    const notifDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    const diffInDays = Math.floor((today - notifDate) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} day/s ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const notificationItems = (
    <div style={{ 
      width: 350, 
      maxHeight: 400, 
      overflow: 'auto',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
      border: '1px solid #d9d9d9'
    }}>
      <div style={{ 
        padding: '12px 16px', 
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Text strong>Notifications {unreadCount > 0 && `(${unreadCount})`}</Text>
      </div>
      
      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spin />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Loading notifications...
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: '20px' }}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
          />
        </div>
      ) : (
        <List
          itemLayout="vertical"
          dataSource={notifications}
          renderItem={(notification) => {
            const notificationType = getNotificationType(notification);
            return (
            <List.Item
              key={notification.id}
              style={{
                padding: '12px 16px',
                backgroundColor: notificationType === 'pending_approval' ? '#f6ffed' : notificationType === 'updated_after_rejection' ? '#e6f7ff' : '#fff2f0',
                borderLeft: notificationType === 'pending_approval' ? '3px solid #52c41a' : notificationType === 'updated_after_rejection' ? '3px solid #1890ff' : '3px solid #ff4d4f',
                cursor: 'pointer',
              }}
              onClick={() => handleNotificationClick(notification)}
            >
              <div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: 4
                }}>
                  <Text strong style={{ fontSize: '14px', color: notificationType === 'pending_approval' ? '#52c41a' : notificationType === 'updated_after_rejection' ? '#1890ff' : '#d32f2f' }}>
                    {notificationType === 'pending_approval' ? 'Pending Approval' : notificationType === 'updated_after_rejection' ? 'Updated Direct Order' : 'Direct Order Rejected'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNotificationTime(notification)}
                  </Text>
                </div>
                
                <Text style={{ fontSize: '13px', display: 'block', marginBottom: 4 }}>
                  Order #{notification.doId || notification.orderId} ({notification.doRef || notification.orderRef})
                </Text>
                
                <Text style={{ fontSize: '12px', display: 'block', marginBottom: 4, color: '#666' }}>
                  Supplier: {notification.doSupplier || notification.supplier} • Total: €{parseFloat(notification.doTotalCost || notification.totalCost || 0).toFixed(2)}
                </Text>
                
                {notificationType === 'pending_approval' && (
                  <Text style={{ fontSize: '12px', display: 'block', color: '#666' }}>
                    Requester: {notification.requesterName}
                  </Text>
                )}
                
                {(notificationType === 'rejection' || notificationType === 'updated_after_rejection') && notification.rejectionReason && (
                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic', color: '#d32f2f' }}>
                    Reason: {notification.rejectionReason}
                  </Text>
                )}
                
                {notification.notificationMessage && (
                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                    {notification.notificationMessage}
                  </Text>
                )}
              </div>
            </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  return (
    <>
      <Dropdown
        popupRender={() => notificationItems}
        trigger={['click']}
        placement={placement}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        {renderTrigger ? (
          renderTrigger({ unreadCount, open: dropdownOpen })
        ) : (
          <Button 
            type="text" 
            icon={
              <Badge count={unreadCount} size="small">
                <BellOutlined style={{ fontSize: '18px' }} />
              </Badge>
            }
            style={{ border: 'none', boxShadow: 'none' }}
          />
        )}
      </Dropdown>

      {/* Modal for viewing direct order (pending_approval and updated_after_rejection) */}
      {selectedDirectOrder && viewModalVisible && (
        <DirectOrderViewModal
          visible={viewModalVisible}
          directOrder={selectedDirectOrder}
          onClose={handleCloseViewModal}
        />
      )}

      {/* Modal for editing direct order (rejection) */}
      {selectedDirectOrder && editModalVisible && (
        <DirectOrderEditModal
          visible={editModalVisible}
          directOrder={selectedDirectOrder}
          onClose={handleCloseEditModal}
          onSave={handleSaveEditModal}
        />
      )}
    </>
  );
};

export default NotificationBell;
